import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { STATION_TYPES } from '../../data/domain';
import type { SnapshotFrame, Station } from '../../data/types';
import { pointToLatLng, SNAPSHOT_LAYERS, stationPosition, TILES_HIGH, TILES_WIDE, tileUrl, zoomFrame } from './snapshot';

export interface SiteSnapshotHandle { element: HTMLDivElement | null; zoomBy(delta: number): void }

interface Props {
  frame: SnapshotFrame;
  stations: Station[];
  onFrameChange(frame: SnapshotFrame): void;
  onPlace(point: { lat: number; lng: number }): void;
  onRemove(index: number): void;
}

/**
 * Satellite snapshot for placing bait stations. One finger or the mouse pans, a tap places a station, two
 * fingers pinch-zoom, and the wheel zooms around the cursor.
 */
export const SiteSnapshot = forwardRef<SiteSnapshotHandle, Props>(function SiteSnapshot({ frame, stations, onFrameChange, onPlace, onRemove }, ref) {
  const element = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const frameRef = useRef(frame); frameRef.current = frame;
  const drag = useRef<{ id: number; x: number; y: number; cropX: number; cropY: number } | null>(null);
  const touches = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ distance: number; scale: number } | null>(null);

  useImperativeHandle(ref, () => ({
    get element() { return element.current; },
    zoomBy(delta: number) { onFrameChange(zoomFrame(frameRef.current, frameRef.current.crop.scale + delta, { x: size.width / 2, y: size.height / 2 })); }
  }), [onFrameChange, size]);

  useEffect(() => {
    const node = element.current;
    if (!node) return;
    const observer = new ResizeObserver(() => setSize({ width: node.clientWidth, height: node.clientHeight }));
    observer.observe(node);
    // React's onWheel is passive; zooming needs preventDefault so the page does not scroll.
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = node.getBoundingClientRect();
      const step = Math.min(0.05, Math.max(0.0125, Math.abs(event.deltaY) * 0.0005));
      onFrameChange(zoomFrame(frameRef.current, frameRef.current.crop.scale + (event.deltaY < 0 ? step : -step), { x: event.clientX - rect.left, y: event.clientY - rect.top }));
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => { observer.disconnect(); node.removeEventListener('wheel', onWheel); };
  }, [onFrameChange]);

  const offset = (event: { clientX: number; clientY: number }) => { const rect = element.current!.getBoundingClientRect(); return { x: event.clientX - rect.left, y: event.clientY - rect.top }; };
  const pinchState = () => { const [a, b] = [...touches.current.values()]; return { distance: Math.hypot(a.x - b.x, a.y - b.y), centre: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } }; };

  return (
    <div className="site-snapshot" id="siteSnapshot" ref={element} role="application" aria-label="Detailed site snapshot for bait station placement"
      onPointerDown={(event) => {
        if ((event.target as HTMLElement).closest('.snapshot-station')) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        if (event.pointerType === 'touch') {
          touches.current.set(event.pointerId, offset(event));
          if (touches.current.size === 2) { drag.current = null; pinch.current = { distance: pinchState().distance, scale: frame.crop.scale }; return; }
        }
        drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, cropX: frame.crop.x, cropY: frame.crop.y };
      }}
      onPointerMove={(event) => {
        if (event.pointerType === 'touch' && touches.current.has(event.pointerId)) {
          touches.current.set(event.pointerId, offset(event));
          if (pinch.current && touches.current.size >= 2) { const state = pinchState(); onFrameChange(zoomFrame(frame, pinch.current.scale * (state.distance / pinch.current.distance), state.centre)); return; }
        }
        const current = drag.current;
        if (!current || current.id !== event.pointerId) return;
        const dx = event.clientX - current.x; const dy = event.clientY - current.y;
        if (Math.abs(dx) + Math.abs(dy) > 4) onFrameChange({ ...frame, crop: { ...frame.crop, x: current.cropX + dx, y: current.cropY + dy } });
      }}
      onPointerUp={(event) => {
        const wasPinching = event.pointerType === 'touch' && Boolean(pinch.current);
        if (event.pointerType === 'touch') touches.current.delete(event.pointerId);
        if (wasPinching) { if (touches.current.size < 2) pinch.current = null; drag.current = null; return; }
        const current = drag.current;
        if (!current || current.id !== event.pointerId) return;
        drag.current = null;
        // A press that did not move is a tap: place a station there.
        if (Math.abs(event.clientX - current.x) + Math.abs(event.clientY - current.y) <= 4) {
          const point = pointToLatLng(offset(event).x, offset(event).y, frame, size);
          if (point) onPlace(point);
        }
      }}
      onPointerCancel={(event) => { touches.current.delete(event.pointerId); if (touches.current.size < 2) pinch.current = null; if (drag.current?.id === event.pointerId) drag.current = null; }}
    >
      <div className="snapshot-scene" aria-label="Satellite site snapshot with roads and labels" style={{ transform: `translate(${frame.crop.x}px, ${frame.crop.y}px) scale(${frame.crop.scale})` }}>
        {Array.from({ length: TILES_HIGH }, (_, row) => Array.from({ length: TILES_WIDE }, (_, col) => {
          const style = { left: `${(col / TILES_WIDE) * 100}%`, top: `${(row / TILES_HIGH) * 100}%`, width: `${100 / TILES_WIDE}%`, height: `${100 / TILES_HIGH}%` };
          return SNAPSHOT_LAYERS.map((layer, index) => (
            <img key={`${frame.originX}-${frame.originY}-${row}-${col}-${layer}`} className={`snapshot-tile${index ? ' snapshot-reference' : ''}`} data-col={col} data-row={row} draggable={false} crossOrigin="anonymous" style={style} src={tileUrl(layer, frame.originX + col, frame.originY + row)} alt="" />
          ));
        }))}
      </div>
      {size.width > 0 && stations.map((station, index) => {
        const position = stationPosition(station, frame, size);
        return (
          <button key={index} type="button" className={`snapshot-station type-${station.type}`} style={{ left: `${position.x}px`, top: `${position.y}px` }} aria-label={`Remove ${STATION_TYPES[station.type].label} station ${index + 1}`}
            onClick={(event) => { event.stopPropagation(); onRemove(index); }}>{index + 1}</button>
        );
      })}
    </div>
  );
});
